/**
 * Validation Helper Functions
 * Common utilities used across general and sensor validation
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldError } from '../../types/validation';
import { validateFieldValue, makeHint } from '../../utils/validators';

/**
 * Helper function to get byte length
 */
export const getByteLength = (str: string): number => {
  return new TextEncoder().encode(str).length;
};

/**
 * Custom hook for validation helpers
 */
export const useValidationHelpers = () => {
  const { t } = useTranslation('config');

  /**
   * Validate individual field with hint generation
   */
  const validateAndHint = useCallback(
    (
      fieldName: string,
      value: any,
      constraintInfo: any,
      errors: Record<string, FieldError>,
      hints: Record<string, string>,
      hintKey?: string
    ) => {
      // constraintInfo가 없어도 빈값 검증은 수행
      const error = validateFieldValue(fieldName, value, constraintInfo, t);
      if (error) {
        errors[fieldName] = { hasError: true, message: error };
      } else {
        // 에러가 없을 때만 hint 표시 (constraintInfo가 있고 ranges가 있으면)
        if (constraintInfo?.ranges) {
          const hint = makeHint(hintKey || `hint_${fieldName}`, constraintInfo, t);
          if (hint) hints[fieldName] = hint;
        }
      }
    },
    [t]
  );

  return {
    validateAndHint,
    t,
  };
};
